import { numberHelpers } from '@approvalmax/utils';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import { backend, domain } from 'modules/data';
import { NetSuiteContext } from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { GetOcr } from 'shared/data/webApp/v2';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteVendorFieldSettings } from 'shared/hooks';

import { CustomFieldsState } from '../../data/types';
import { getCustomFieldsTransfer, getVendorWebAddressTransfer, isVendorWebAddressValid } from '../../helpers';
import { isFieldMandatory, isMandatoryFieldEmpty } from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import {
    NetSuiteVendorAddressListItemIds,
    NetSuiteVendorFields,
    NetSuiteVendorFieldsTransfer,
} from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.types';
import { NetSuiteSubsidiaryListItemIds } from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.types';
import {
    addressListItemIdPrefix,
    defaultAddressLine,
    subsidiaryListItemIdPrefix,
} from './NetSuiteVendorForm.constants';
import { messages } from './NetSuiteVendorForm.messages';

export const isVendorSubsidiaryEmpty = (subsidiary: NetSuiteSubsidiaryListItemIds) => {
    return numberHelpers.isNumber(subsidiary.creditLimit) && !subsidiary.subsidiary && !subsidiary.taxCode;
};

export const isVendorAddressEmpty = (address: NetSuiteVendorAddressListItemIds) => {
    return (
        !address.addressee &&
        !address.attention &&
        !address.city &&
        !address.country &&
        !address.label &&
        !address.phone &&
        !address.primaryAddress &&
        !address.secondaryAddress &&
        !address.state &&
        !address.zip
    );
};

export const getVendorTransfer = (
    fieldValues: NetSuiteVendorFieldsTransfer,
    customFieldsValues?: CustomFieldsState
): backend.transfers.CreateNetSuiteVendorData => {
    const primarySubsidiaryItem = fieldValues.subsidiaries?.find(
        ({ subsidiary }) => subsidiary?.id === fieldValues.primarySubsidiary?.id
    );

    return {
        type: fieldValues.isPerson ? domain.NetSuiteVendorType.Individual : domain.NetSuiteVendorType.Company,
        companyName: fieldValues.companyName || null,
        webAddress: getVendorWebAddressTransfer(fieldValues.webAddress),
        salutation: fieldValues.salutation || null,
        firstName: fieldValues.firstName || null,
        lastName: fieldValues.lastName || null,
        middleName: fieldValues.middleName || null,
        jobTitle: fieldValues.jobTitle || null,
        categoryId: fieldValues.category?.id || null,
        comments: fieldValues.comments || null,
        email: fieldValues.email || null,
        phone: fieldValues.phone || null,
        altPhone: fieldValues.altPhone || null,
        fax: fieldValues.fax || null,
        subsidiaryId: fieldValues.primarySubsidiary?.id || null,
        legalName: fieldValues.legalName || null,
        accountNumber: fieldValues.accountNumber || null,
        defaultExpenseAccountId: fieldValues.defaultExpenseAccount?.id || null,
        defaultPayablesAccountId: fieldValues.defaultPayablesAccount?.id || null,
        currencyId: fieldValues.currency?.id || null,
        termsId: fieldValues.terms?.id || null,
        creditLimit: fieldValues.creditLimit ?? primarySubsidiaryItem?.creditLimit ?? null,
        incotermName: fieldValues.incoterm?.text || null,
        taxNumber: fieldValues.taxNumber || null,
        is1099Eligible: fieldValues.is1099Eligible,
        isJobResource: fieldValues.isJobResource,
        addressLines: (fieldValues.addressLines || [])
            .filter((address) => !isVendorAddressEmpty(address))
            .map((addressItem) => ({
                label: addressItem.label || null,
                defaultBilling: !!addressItem.defaultBilling,
                defaultShipping: !!addressItem.defaultShipping,
                primaryAddress: addressItem.primaryAddress || null,
                secondaryAddress: addressItem.secondaryAddress || null,
                additionalAddress: addressItem.additionalAddress || null,
                addressRepresentation: addressItem.addressRepresentation || null,
                addressee: addressItem.addressee || null,
                attention: addressItem.attention || null,
                city: addressItem.city || null,
                country: addressItem.country?.id || null,
                zip: addressItem.zip || null,
                state: addressItem.state || null,
                phone: addressItem.phone || null,
            })),
        currencies: (fieldValues.currencies || []).map((currencyItem) => currencyItem.id),
        subsidiaries: (fieldValues.subsidiaries || [])
            .filter((subsidiary) => !isVendorSubsidiaryEmpty(subsidiary))
            .map(({ subsidiary, creditLimit, taxCode }) => ({
                subsidiaryId: subsidiary?.id || null,
                creditLimit:
                    creditLimit ??
                    (subsidiary?.id === primarySubsidiaryItem?.subsidiary?.id ? fieldValues.creditLimit ?? null : null),
                taxCodeId: taxCode?.id || null,
            })),
        customFields: customFieldsValues ? getCustomFieldsTransfer(Object.values(customFieldsValues)) : null,
    };
};

export const getVendorValidationErrors = ({
    fieldValues,
    fieldSettings,
    attachmentsFieldSettings,
    attachments,
}: {
    fieldValues: NetSuiteVendorFields | null;
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>;
    attachmentsFieldSettings?: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments?: domain.RequestAttachment[];
}): string[] => {
    const errors: string[] = [];

    if (!fieldValues) {
        return errors;
    }

    if (!fieldValues.isPerson) {
        if (isMandatoryFieldEmpty(fieldValues.webAddress, fieldSettings.webAddress)) {
            errors.push(messages.mandatoryError);
        }

        if (!isVendorWebAddressValid(fieldValues.webAddress)) {
            errors.push(messages.invalidWebAddress);
        }
    }

    if (
        attachments &&
        attachmentsFieldSettings &&
        isFieldMandatory(attachmentsFieldSettings.attachments) &&
        attachments.length === 0
    ) {
        errors.push(messages.mandatoryError);
    }

    if (
        fieldValues.isPerson &&
        (!fieldValues.firstName ||
            !fieldValues.lastName ||
            isMandatoryFieldEmpty(fieldValues.salutation, fieldSettings.salutation) ||
            isMandatoryFieldEmpty(fieldValues.jobTitle, fieldSettings.jobTitle))
    ) {
        errors.push(messages.mandatoryError);
    }

    const primarySubsidiaryItem = fieldValues.subsidiaries.find(
        ({ subsidiary }) => subsidiary?.id === fieldValues.primarySubsidiary?.id
    );

    if (
        !fieldValues.primarySubsidiary ||
        isMandatoryFieldEmpty(fieldValues.currency, fieldSettings.currency) ||
        isMandatoryFieldEmpty(fieldValues.category, fieldSettings.category) ||
        isMandatoryFieldEmpty(fieldValues.comments, fieldSettings.comments) ||
        isMandatoryFieldEmpty(fieldValues.email, fieldSettings.email) ||
        isMandatoryFieldEmpty(fieldValues.phone, fieldSettings.phone) ||
        isMandatoryFieldEmpty(fieldValues.altPhone, fieldSettings.altPhone) ||
        isMandatoryFieldEmpty(fieldValues.fax, fieldSettings.fax) ||
        isMandatoryFieldEmpty(fieldValues.legalName, fieldSettings.legalName) ||
        isMandatoryFieldEmpty(fieldValues.accountNumber, fieldSettings.accountNumber) ||
        isMandatoryFieldEmpty(fieldValues.defaultExpenseAccount, fieldSettings.defaultExpenseAccount) ||
        isMandatoryFieldEmpty(fieldValues.defaultPayablesAccount, fieldSettings.defaultPayablesAccount) ||
        isMandatoryFieldEmpty(fieldValues.terms, fieldSettings.terms) ||
        (isFieldMandatory(fieldSettings.creditLimit) &&
            !numberHelpers.isNumber(fieldValues.creditLimit) &&
            !numberHelpers.isNumber(primarySubsidiaryItem?.creditLimit)) ||
        isMandatoryFieldEmpty(primarySubsidiaryItem?.taxCode, fieldSettings.taxCode) ||
        isMandatoryFieldEmpty(fieldValues.incoterm, fieldSettings.incoterm) ||
        isMandatoryFieldEmpty(fieldValues.taxNumber, fieldSettings.taxNumber)
    ) {
        errors.push(messages.mandatoryError);
    }

    if (!fieldValues.subsidiaries) {
        return uniq(errors);
    }

    const selectedSubsidiariesIds = new Set();

    for (const item of fieldValues.subsidiaries) {
        const id = item.subsidiary?.id;

        if (id) {
            if (selectedSubsidiariesIds.has(id)) {
                errors.push(messages.duplicatedSubsidiary);

                break;
            } else {
                selectedSubsidiariesIds.add(id);
            }
        }
    }

    if (!fieldValues.addressLines || errors.includes(messages.mandatoryError)) {
        return uniq(errors);
    }

    for (const address of fieldValues.addressLines) {
        if (
            !isVendorAddressEmpty(address) &&
            (isMandatoryFieldEmpty(address.country, fieldSettings.addressLinesCountry) ||
                isMandatoryFieldEmpty(address.attention, fieldSettings.addressLinesAttention) ||
                isMandatoryFieldEmpty(address.addressee, fieldSettings.addressLinesAddressee) ||
                isMandatoryFieldEmpty(address.phone, fieldSettings.addressLinesPhone) ||
                isMandatoryFieldEmpty(address.primaryAddress, fieldSettings.addressLinesPrimaryAddress) ||
                isMandatoryFieldEmpty(address.secondaryAddress, fieldSettings.addressLinesSecondaryAddress) ||
                isMandatoryFieldEmpty(address.state, fieldSettings.addressLinesState) ||
                isMandatoryFieldEmpty(address.city, fieldSettings.addressLinesCity) ||
                isMandatoryFieldEmpty(address.zip, fieldSettings.addressLinesZip))
        ) {
            errors.push(messages.mandatoryError);

            break;
        }
    }

    return uniq(errors);
};

export const generateUniqueSubsidiaryListItemId = () => uniqueId(subsidiaryListItemIdPrefix);

export const generateUniqueAddressListItemId = () => uniqueId(addressListItemIdPrefix);

export const extractOcrAddress = (ocrDocument: GetOcr['response']['document']) => {
    return ocrDocument?.vendor?.address
        ? {
              ...defaultAddressLine,
              id: generateUniqueAddressListItemId(),
              primaryAddress: ocrDocument.vendor.address?.street || '',
              city: ocrDocument.vendor.address?.city || '',
              zip: ocrDocument.vendor.address?.postalCode || '',
              state: ocrDocument.vendor.address?.region || '',
          }
        : null;
};

export const extractOcrCurrency = (
    ocrDocument: GetOcr['response']['document'],
    currencies: NetSuiteContext['currencies']
) => {
    return ocrDocument?.currencyCode
        ? {
              id: ocrDocument.currencyCode,
              text: currencies.find((currency) => currency.id === ocrDocument.currencyCode)?.text || '',
          }
        : null;
};
